import { createServerFn } from "@tanstack/react-start";
import type { DepartmentListItem, DepartmentDetail } from "@/hooks/useDepartments";
import type { Doctor, DoctorDetail } from "@/hooks/useDoctors";
import type { ArticleListItem, ArticleDetail } from "@/hooks/useArticles";
import type { HealthPackage } from "@/hooks/usePackages";
import type { HomeSettings } from "@/hooks/useHomeSettings";
import type { AboutUsData } from "@/hooks/useAboutUs";
import type { ContactSettings } from "@/hooks/useContactSettings";

const FRAPPE_URL = process.env.FRAPPE_URL;

async function frappeGet<T>(method: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(`/api/method/${method}`, FRAPPE_URL);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Frappe API error: ${res.status}`);
  const data = await res.json();
  return data.message as T;
}

export const getDepartments = createServerFn({ method: "GET" }).handler(async () => {
  return frappeGet<DepartmentListItem[]>("health_web.api.departments.get_departments");
});

export const getDepartment = createServerFn({ method: "GET" })
  .validator((name: string) => name)
  .handler(async ({ data }) => {
    return frappeGet<DepartmentDetail>("health_web.api.departments.get_department", { name: data });
  });

export const getDoctors = createServerFn({ method: "GET" }).handler(async () => {
  return frappeGet<Doctor[]>("health_web.api.doctors.get_doctors");
});

export const getDoctor = createServerFn({ method: "GET" })
  .validator((name: string) => name)
  .handler(async ({ data }) => {
    return frappeGet<DoctorDetail>("health_web.api.doctors.get_doctor", { name: data });
  });

export const getArticles = createServerFn({ method: "GET" }).handler(async () => {
  return frappeGet<ArticleListItem[]>("health_web.api.articles.get_articles");
});

export const getArticle = createServerFn({ method: "GET" })
  .validator((slug: string) => slug)
  .handler(async ({ data }) => {
    return frappeGet<ArticleDetail>("health_web.api.articles.get_article", { slug: data });
  });

export const getHealthPackages = createServerFn({ method: "GET" }).handler(async () => {
  return frappeGet<HealthPackage[]>("health_web.api.packages.get_packages");
});

export const getHomeSettings = createServerFn({ method: "GET" }).handler(async () => {
  return frappeGet<HomeSettings>("health_web.api.home.get_home_settings");
});

export const getAboutUs = createServerFn({ method: "GET" }).handler(async () => {
  return frappeGet<AboutUsData>("health_web.api.about.get_about_us");
});

export const getContactSettings = createServerFn({ method: "GET" }).handler(async () => {
  return frappeGet<ContactSettings>("health_web.api.contact.get_contact_settings");
});